import React, { useState } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { Column, Container, Icon, List, Row } from '@pella/sash-framework';
import { CTA, Image } from '../../';
import { isValidArray } from '../../utilities/validations';
import Socials from '../footer/socials';
import Logo from '../logo';

const CareersFooter = () => {
	const [selected, setSelected] = useState(null);
	const data = useStaticQuery(query);

	if (!data?.footer) return false;

	const onNavTitleClick = (e) => {
		const idx = Number(e.currentTarget.dataset.idx);

		if (selected === idx) {
			setSelected('');
		} else {
			setSelected(idx);
		}
	};

	return (
		<footer
			className='careersFooter block'
			data-testid='careers-footer'
		>
			{data.footer.pre_footer && (
				<div className='careersFooter-top bg-neutral-200 py-5'>
					<Container
						className='careersFooter-topContainer'
						fluid
					>
						<Row
							align='center'
							justify='center'
						>
							{data.footer.pre_footer.awards?.[0] &&
								data.footer.pre_footer.awards.map((award, index) => (
									<Column
										xs={12}
										sm='auto'
										md='fluid'
										lg='auto'
										key={index}
									>
										<span className='mb-4 block rounded bg-white p-4 text-center md:mb-0 md:p-5'>
											<Image
												data={award}
												className='mx-auto max-h-[56px] sm:max-h-[46px] md:max-h-[56px]'
											/>
										</span>
									</Column>
								))}

							<Column
								xs='auto'
								className='careersFooter-signup'
							>
								{data.footer.pre_footer.get_notified && <p className='mb-2.5 text-base lg:text-xl'>{data.footer.pre_footer.get_notified}</p>}

								{data.footer.pre_footer.ctas?.[0] && (
									<CTA
										className='justify-center [&_span]:!mb-0 [&_span]:!p-0'
										data={data.footer.pre_footer.ctas[0]}
										wide
									/>
								)}
							</Column>
						</Row>
					</Container>
				</div>
			)}

			{data.footer.footer_categories && (
				<div className='careersFooter-bottom bg-neutral-900 py-5 lg:py-16'>
					<Container
						className='careersFooter-bottomContainer max-lg:p-0'
						fluid
					>
						<Row noGutters={true}>
							<Column
								xs={12}
								lg={2}
								className='careersFooter-logo text-center xl:text-left'
							>
								<Logo className='w-24 max-md:text-center lg:max-xl:mb-12' />
							</Column>

							{isValidArray(data?.footer?.footer_categories) &&
								data.footer.footer_categories.map((nav, index) => (
									<Column
										key={index}
										xs={12}
										lg='fluid'
									>
										{!nav.ctas_secondary?.[0] && nav.ctas?.[0] && (
											<p className='text-base font-light uppercase tracking-2px text-white'>
												<CTA
													data={nav.ctas[0]}
													className='block px-5 py-6 text-white no-underline hover:text-neutral-200 hover:underline hover:decoration-primary max-lg:border-b max-lg:border-b-neutral-600 lg:p-0'
												/>
											</p>
										)}

										{nav.ctas_secondary?.[0] && (
											<>
												{nav.heading && <p className='hidden text-base font-light uppercase tracking-2px text-white lg:mb-6 lg:block'>{nav.heading}</p>}

												<button
													data-idx={index}
													className={`relative w-full px-5 py-6 text-left text-base font-light uppercase tracking-2px text-white hover:underline hover:decoration-primary max-lg:border-b max-lg:border-b-neutral-600 lg:hidden lg:p-0 ${selected === index ? 'bg-white/10' : null}`}
													onClick={onNavTitleClick}
													aria-expanded={selected === index}
												>
													{nav.heading}
													<span className='careersFooter-navIcon absolute right-5 top-1/2 -mt-[17px] h-[34px] w-[34px] rounded-full bg-primary text-center lg:hidden'>
														<Icon
															name={selected === index ? 'remove' : 'add'}
															className='mx-auto mt-1 block text-black'
														/>
													</span>
												</button>

												<List
													bullets='none'
													className={selected === index ? '' : 'hidden lg:block'}
												>
													{nav.ctas_secondary.map((subnav, index) => (
														<li
															key={index}
															className='text-sm font-light lg:my-4'
														>
															<CTA
																data={subnav}
																className='text-white no-underline hover:text-neutral-200 hover:underline hover:decoration-primary max-lg:block max-lg:border-b max-lg:border-b-neutral-600 max-lg:px-5 max-lg:py-4'
															/>
														</li>
													))}
												</List>
											</>
										)}
									</Column>
								))}
						</Row>
					</Container>
				</div>
			)}

			{data.copyright && (
				<div className='careersFooter-copyright bg-neutral-900 py-5 lg:border-t lg:border-t-neutral-750'>
					<Container
						className='careersFooter-copyrightContainer'
						fluid
					>
						<Row
							align='center'
							verticalGutters={true}
						>
							<Column
								xs={12}
								xl='fluid'
							>
								{data.copyright.copyright && (
									<p className='text-[13px] uppercase text-white'>
										&#169; {new Date().getFullYear()} {data.copyright.copyright}
									</p>
								)}

								{data.copyright.copyright_links?.ctas?.[0] && (
									<List
										bullets='none'
										className='mt-0 flex flex-wrap space-x-7'
									>
										{data.copyright.copyright_links.ctas.map((link, index) => (
											<li
												key={index}
												className='my-2.5 max-w-full flex-none text-[13px] md:mb-0 md:mt-4'
											>
												<CTA
													data={link}
													className='text-white decoration-transparent hover:underline hover:decoration-primary'
												/>
											</li>
										))}
									</List>
								)}
							</Column>

							{data.socials?.cta?.[0] && (
								<Column
									xs={12}
									xl='auto'
									className='-order-1 mb-5 xl:order-1 xl:mb-0'
								>
									<Socials data={data.socials.cta} />
								</Column>
							)}
						</Row>
					</Container>
				</div>
			)}
		</footer>
	);
};

export default CareersFooter;

export const query = graphql`
	query {
		footer: contentstackCareersFooter {
			pre_footer {
				awards {
					...Image
					image {
						gatsbyImageData(height: 56, quality: 90)
					}
				}
				get_notified
				ctas {
					...CTA
				}
			}
			footer_categories {
				heading
				ctas {
					...CTA
				}
				ctas_secondary {
					...CTA
				}
			}
		}

		copyright: contentstackCareersCopyright {
			copyright
			copyright_links {
				ctas {
					...CTA
				}
			}
		}

		socials: contentstackSocialMedia {
			cta {
				...CTA
			}
		}
	}
`;
